import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Users,
  ChevronRight,
  GraduationCap,
  Car,
  Hotel,
  Sparkles,
  Compass,
  Globe,
  Heart,
  Wind,
  Mail,
  Phone,
  ExternalLink,
  MapPin,
} from 'lucide-react';

const Aboutus = () => {
  const navigate = useNavigate();

  const companyDetails = {
    legalName: 'HimalayanZenith Ventures Private Limited',
    cin: 'U74900WB2025PTC278739',
    gstin: '09AAHCH9024K1Z7',
    registeredAddress:
      'Registered Office: Siliguri, Darjeling(Dist), West Bengal - 734001, India',
    email: 'contact@thegreathimalayas.com',
    phone: '+91 9883587965',
    nodalOfficer: 'Grievance Redressal Officer',
  };

  const marketplaceData = [
    {
      icon: <Car size={32} />,
      title: 'Cabs & Mobility',
      tagline: 'Shared Mobility',
      features: ['Verified Fleet', 'Networked Routes', 'Transparent Pricing'],
      color: 'blue',
    },
    {
      icon: <Hotel size={32} />,
      title: 'Curated Stays',
      tagline: 'Accommodations',
      features: ['Luxury Hotels', 'Boutique Stays', 'Verified Hygiene'],
      color: 'slate',
    },
    {
      icon: <Compass size={32} />,
      title: 'Tour Expeditions',
      tagline: 'Global Programs',
      features: ['Expert Guides', 'Safety First', 'Full Logistics'],
      color: 'blue',
    },
    {
      icon: <Sparkles size={32} />,
      title: 'Wellness Programs',
      tagline: 'The Marketplace',
      features: ['Yoga & Healing', 'Retreat Booking', 'Holistic Experts'],
      color: 'slate',
    },
  ];

  const systemsData = [
    {
      title: 'Standardized Networks',
      desc: 'Predictable pricing for shared cab networks',
      icon: <Car size={18} />,
    },
    {
      title: 'Enterprise Vetting',
      desc: 'Gold-standard audits for hotels and stays',
      icon: <ShieldCheck size={18} />,
    },
    {
      title: 'Tech-Driven Wellness',
      desc: 'Technology marketplace for wellness programs',
      icon: <Sparkles size={18} />,
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      <section className="relative h-[85vh] flex items-center overflow-hidden bg-slate-900 mt-20 md:mt-32">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/40 to-slate-900 z-10" />
          <img
            src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&q=80&w=2000"
            alt="The Great Himalayas"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-[1px] w-16 bg-blue-500"></div>
              <span className="text-blue-400 font-black uppercase tracking-[0.4em] text-[10px]">
                A Noble Vision
              </span>
            </div>

            <h1 className="text-6xl md:text-[110px] font-black mb-8 leading-[0.9] tracking-tighter">
              Where Rigor Meets <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-sky-200 italic font-serif">
                The Wild.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed max-w-2xl font-light italic">
              "The mountains are calling, and we have engineered the path."
            </p>

            <div className="flex flex-wrap gap-6">
              <button
                onClick={() => navigate('/')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-5 rounded-full font-black text-sm uppercase tracking-widest transition-all flex items-center gap-3 shadow-2xl shadow-blue-900/20"
              >
                Explore The Marketplace <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <div className="space-y-10">
              <div className="inline-block p-3 bg-blue-50 rounded-2xl">
                <Heart className="text-blue-600 w-6 h-6" />
              </div>

              <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight tracking-tight">
                Born from the <span className="text-blue-600">Peaks</span>, <br />
                Guided by <span className="text-slate-400">Precision.</span>
              </h2>

              <div className="space-y-6 text-slate-600 text-lg leading-relaxed font-medium">
                <p>
                  The Great Himalayas was conceived not just as a travel brand,
                  but as a noble response to the chaotic beauty of our majestic
                  mountains. We saw a gap between the soulful call of the peaks
                  and the logistical complexity of reaching them safely.
                </p>
                <p>
                  Today, we stand as a complete ecosystem — a single-window
                  portal that integrates mobility, luxury stays, and wellness
                  programs into a seamless, high-trust marketplace.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="h-64 rounded-[2rem] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80"
                    alt="Mountain peaks"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="bg-slate-900 p-8 rounded-[2rem] text-white text-center">
                  <h4 className="text-3xl font-black mb-2">100%</h4>
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">
                    Vetted Network
                  </p>
                </div>
              </div>

              <div className="space-y-6 pt-12">
                <div className="bg-blue-600 p-8 rounded-[2rem] text-white">
                  <Wind className="w-8 h-8 mb-4 opacity-50" />
                  <p className="text-sm font-bold leading-relaxed italic">
                    "Bringing corporate discipline to the mountain winds."
                  </p>
                </div>

                <div className="h-64 rounded-[2rem] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1533130061792-64b345e4a833?auto=format&fit=crop&q=80"
                    alt="Mountain travel"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="bg-white p-12 rounded-[3rem] shadow-2xl border border-slate-100 relative group transition-all duration-500">
              <div className="absolute top-10 right-10 opacity-5 group-hover:opacity-10 transition-opacity">
                <GraduationCap size={150} />
              </div>

              <div className="flex items-center gap-6 mb-10">
                <div className="bg-blue-600 p-5 rounded-3xl shadow-lg shadow-blue-100">
                  <GraduationCap className="text-white w-10 h-10" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                    The IIT Madras DNA
                  </h2>
                  <p className="text-blue-600 font-black text-xs tracking-[0.2em] uppercase mt-1">
                    Systems Engineering & Strategy
                  </p>
                </div>
              </div>

              <div className="space-y-8">
                <p className="text-slate-600 text-lg leading-relaxed font-medium">
                  Managing a cross-functional marketplace in challenging terrains
                  requires high-level systems thinking. Our leadership team
                  consists of <strong>IIT Madras alumni</strong> with{' '}
                  <strong>12+ years of corporate experience</strong> in building
                  scalable, reliable service ecosystems.
                </p>

                <div className="grid gap-4">
                  {systemsData.map((item, i) => (
                    <div
                      key={i}
                      className="flex gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 group hover:bg-blue-50 hover:border-blue-200 transition-all"
                    >
                      <div className="text-blue-600 mt-1">{item.icon}</div>
                      <div>
                        <h4 className="font-black text-slate-900 text-sm uppercase tracking-tight">
                          {item.title}
                        </h4>
                        <p className="text-slate-500 text-xs mt-1 font-bold">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-10">
              <h3 className="text-4xl md:text-6xl font-black text-slate-900 leading-[1] tracking-tighter">
                Engineering <br />
                <span className="text-blue-600">The Experience.</span>
              </h3>

              <p className="text-slate-500 text-xl leading-relaxed italic font-medium">
                "We applied the same discipline found in global supply chains to
                the unpredictable trails of the Himalayas."
              </p>

              <div className="grid grid-cols-2 gap-8">
                <div className="text-center p-10 bg-white rounded-3xl shadow-xl border-b-[6px] border-blue-700 transform hover:-translate-y-2 transition-all">
                  <p className="text-5xl font-black text-slate-900 mb-2">12+</p>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Years Corporate Leadership
                  </p>
                </div>

                <div className="text-center p-10 bg-white rounded-3xl shadow-xl border-b-[6px] border-slate-800 transform hover:-translate-y-2 transition-all">
                  <p className="text-5xl font-black text-slate-900 mb-2">IIT-M</p>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Engineering Foundation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-24">
            <h2 className="text-5xl font-black mb-6 tracking-tight">
              The Unified Marketplace
            </h2>
            <p className="text-blue-600 font-bold tracking-widest uppercase text-sm">
              One Window. Infinite Horizons.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {marketplaceData.map((item, index) => (
              <MarketplaceCard key={index} {...item} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 p-20 opacity-10 pointer-events-none">
          <Globe size={600} strokeWidth={1} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-24">
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-black mb-8 flex items-center gap-4">
                <ShieldCheck className="text-blue-600 w-10 h-10" />
                Governance
              </h2>

              <p className="text-slate-400 text-xl leading-relaxed mb-10 font-medium">
                <strong>{companyDetails.legalName}</strong> is built on the
                bedrock of corporate accountability. We offer absolute financial
                transparency and ethical standard operating procedures.
              </p>

              <div className="grid gap-8 border-t border-white/10 pt-10">
                <div className="flex items-start gap-4">
                  <MapPin className="text-blue-600 shrink-0 mt-1" />
                  <div>
                    <h4 className="text-[10px] font-black uppercase text-slate-500 tracking-[0.3em] mb-2">
                      Registered Office
                    </h4>
                    <p className="text-sm text-slate-300 font-medium leading-relaxed italic">
                      {companyDetails.registeredAddress}
                    </p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-[10px] font-black uppercase text-slate-500 tracking-[0.3em] mb-3">
                      Corporate ID (CIN)
                    </h4>
                    <a
                      href="https://www.mca.gov.in/mcafoportal/viewCompanyMasterData.do"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-2xl font-mono text-blue-600 font-bold hover:text-blue-400 transition-colors flex items-center gap-2 group"
                    >
                      {companyDetails.cin}
                      <ExternalLink
                        size={16}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </a>
                    <p className="text-[9px] text-slate-600 mt-2 italic font-bold">
                      Verify on MCA Master Data Portal
                    </p>
                  </div>

                  <div>
                    <h4 className="text-[10px] font-black uppercase text-slate-500 tracking-[0.3em] mb-3">
                      GST Authority
                    </h4>
                    <a
                      href="https://services.gst.gov.in/services/searchtp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-2xl font-mono text-blue-600 font-bold hover:text-blue-400 transition-colors flex items-center gap-2 group"
                    >
                      {companyDetails.gstin}
                      <ExternalLink
                        size={16}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </a>
                    <p className="text-[9px] text-slate-600 mt-2 italic font-bold">
                      Verify via GST Search Taxpayer
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:w-1/2 bg-white/5 backdrop-blur-3xl p-12 rounded-[3rem] border border-white/10 w-full shadow-2xl">
              <h3 className="text-xl font-black mb-8 text-center uppercase tracking-widest text-slate-400">
                Grievance Redressal
              </h3>

              <div className="space-y-6">
                <div className="p-6 bg-white/5 rounded-2xl border border-white/5">
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">
                    Statutory Contact Information
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter flex items-center gap-2 italic">
                        <Users size={14} /> {companyDetails.nodalOfficer}
                      </span>
                      <span className="text-sm font-black text-slate-200 uppercase tracking-widest">
                        Nodal Authority
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter flex items-center gap-2 italic">
                        <Mail size={14} /> Email Support
                      </span>
                      <a
                        href={`mailto:${companyDetails.email}`}
                        className="text-sm font-black text-blue-600 hover:underline uppercase tracking-tighter"
                      >
                        {companyDetails.email}
                      </a>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter flex items-center gap-2 italic">
                        <Phone size={14} /> Grievance Helplines
                      </span>
                      <span className="text-sm font-black text-slate-200">
                        {companyDetails.phone}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-[9px] text-slate-500 text-center leading-relaxed">
                  In compliance with Rule 26 of Companies (Incorporation)
                  Rules, 2014, and the Companies Act, 2013.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-40 text-center bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-5xl md:text-7xl font-black text-slate-900 mb-8 tracking-tighter">
            Ready for the Ascent?
          </h2>
          <p className="text-slate-500 text-xl mb-12 font-medium italic">
            "Your journey is our responsibility. Our precision is your peace of
            mind."
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={() => navigate('/partnerwithus')}
              className="bg-slate-900 text-white px-12 py-5 rounded-full font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all shadow-2xl"
            >
              Partner With Us
            </button>
            <button
              onClick={() => navigate('/contactus')}
              className="bg-white text-slate-900 border-2 border-slate-200 px-12 py-5 rounded-full font-black text-sm uppercase tracking-widest hover:border-blue-700 hover:text-blue-700 transition-all"
            >
              Contact Experts
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

const MarketplaceCard = ({ icon, title, tagline, features, color }) => {
  const bgMain = color === 'blue' ? 'bg-blue-700' : 'bg-slate-900';
  const shadow =
    color === 'blue' ? 'shadow-blue-200/50' : 'shadow-slate-200/50';

  return (
    <div className="bg-white p-10 rounded-[3rem] border border-slate-100 group hover:shadow-2xl transition-all duration-500 flex flex-col items-center text-center">
      <div
        className={`${bgMain} text-white p-6 rounded-[2rem] mb-8 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-xl ${shadow}`}
      >
        {icon}
      </div>
      <p className="text-blue-600 text-[9px] font-black uppercase tracking-[0.3em] mb-3">
        {tagline}
      </p>
      <h3 className="text-xl font-black text-slate-900 mb-8 tracking-tight leading-none">
        {title}
      </h3>
      <div className="space-y-3 w-full border-t border-slate-50 pt-8">
        {features.map((f, i) => (
          <div
            key={i}
            className="flex items-center justify-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest"
          >
            <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
            {f}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Aboutus;